import { Field } from 'formik';
import React from 'react';
import { useIntl } from 'react-intl';

import {
  CoachingDetailsInput,
  CoachingTopicObject,
} from '../../../api/CoachingForm/schema';
import Select, { Option } from '../FieldComponents/Select';
import Textarea from '../FieldComponents/Textarea';
import { COACHING_TEXT_LIMIT } from '../../../Util';

interface FormFieldProps<ValueType> {
  values: ValueType;
  options?: Option[];
  error: string | string[] | undefined;
  touched: boolean | undefined;
  conditions?: { [key: string]: boolean };
  update?: (props: { target: { name: string; value: string } }) => void;
}

export const Topic: React.FC<
  FormFieldProps<CoachingDetailsInput['coachingTopic']>
> = ({
  values,
  options,
  touched,
  error,
  conditions,
  update,
}: FormFieldProps<
  CoachingDetailsInput['coachingTopic']
>): React.ReactElement => {
  const intl = useIntl();
  return (
    <Field
      as={Select}
      name='coachingTopic'
      data-cy='coaching-details-coaching-topic'
      label={intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_label_required_coaching_topic',
        defaultMessage: 'Coaching topic *',
        description: 'Coaching Topic Label',
      })}
      value={values}
      options={
        Array.isArray(options)
          ? options.map(topic => {
              return !conditions?.contactReviewsExist &&
                topic.value === CoachingTopicObject.QUALITY
                ? {
                    label: intl.formatMessage({
                      id: 'cs_perfportal_coaching_detail_options_topic_quality_review_required',
                      defaultMessage:
                        'Contact quality (contact review required)',
                    }),
                    value: topic.value,
                    disabled: true,
                  }
                : topic;
            })
          : null
      }
      onChange={update}
      search
      errorText={touched && error}
    />
  );
};

export const Contact: React.FC<
  FormFieldProps<CoachingDetailsInput['relatedContacts']>
> = ({
  values,
  options,
  touched,
  error,
  conditions,
}: FormFieldProps<
  CoachingDetailsInput['relatedContacts']
>): React.ReactElement => {
  const intl = useIntl();
  return !conditions?.developmentTopic && conditions?.showContacts ? (
    <Field
      as={Select}
      name='relatedContacts'
      label={
        conditions.contactOptional
          ? intl.formatMessage({
              id: 'cs_perfportal_coaching_detail_label_optional_related_contacts_select_all_that_apply',
              defaultMessage:
                'Related contacts (optional, select all that apply)',
              description: 'Coaching Details Optional Related Contacts',
            })
          : intl.formatMessage({
              id: 'cs_perfportal_coaching_detail_label_required_related_contacts_select_all_that_apply',
              defaultMessage: 'Related contacts (select all that apply) *',
              description: 'Coaching Details Related Contacts',
            })
      }
      value={values}
      options={options}
      search
      errorText={touched && error}
    />
  ) : (
    <> </>
  );
};

export const Tenets: React.FC<
  FormFieldProps<CoachingDetailsInput['tenets']>
> = ({
  values,
  options,
  touched,
  error,
  conditions,
}: FormFieldProps<CoachingDetailsInput['tenets']>): React.ReactElement => {
  const intl = useIntl();

  return !conditions?.developmentTopic && conditions?.contactSelected ? (
    <Field
      as={Select}
      name='tenets'
      label={
        conditions.contactOptional
          ? intl.formatMessage({
              id: 'cs_perfportal_coaching_detail_label_optional_tenets_select_max_two_that_apply',
              defaultMessage: 'Tenets (select up to two that apply)',
              description: 'Coaching Details Tenets Select',
            })
          : intl.formatMessage({
              id: 'cs_perfportal_coaching_detail_label_required_tenets_select_max_two_that_apply',
              defaultMessage: 'Tenets (select up to two that apply) *',
              description: 'Coaching Details Tenets Select',
            })
      }
      value={values}
      options={options?.map(tenet => {
        // Disable unselected options when the limit is reached.
        return conditions.tenetLimitReached &&
          Array.isArray(values) &&
          values.find(t => t === tenet.value) === undefined
          ? { ...tenet, disabled: true }
          : tenet;
      })}
      search
      errorText={touched && error}
    />
  ) : (
    <> </>
  );
};

export const Behaviors: React.FC<
  FormFieldProps<CoachingDetailsInput['behaviors']>
> = ({
  values,
  options,
  touched,
  error,
  conditions,
}: FormFieldProps<CoachingDetailsInput['behaviors']>): React.ReactElement => {
  const intl = useIntl();
  return !conditions?.developmentTopic && conditions?.contactSelected ? (
    <Field
      as={Select}
      name='behaviors'
      label={
        conditions.contactOptional
          ? intl.formatMessage({
              id: 'cs_perfportal_coaching_detail_label_optional_behaviours_select_max_three_that_apply',
              defaultMessage: 'Behaviors (select up to three that apply)',
              description: 'Coaching Details Behaviours Select',
            })
          : intl.formatMessage({
              id: 'cs_perfportal_coaching_detail_label_required_behaviours_select_max_three_that_apply',
              defaultMessage: 'Behaviors (select up to three that apply) *',
              description: 'Coaching Details Behaviours Select',
            })
      }
      value={values}
      options={options?.map(behaviour => {
        // Disable unselected options when the limit is reached.
        return conditions.behaviourLimitReached &&
          Array.isArray(values) &&
          values.find(b => b === behaviour.value) === undefined
          ? {
              ...behaviour,
              disabled: true,
            }
          : behaviour;
      })}
      search
      errorText={touched && error}
    />
  ) : (
    <> </>
  );
};

export const Metric: React.FC<
  FormFieldProps<CoachingDetailsInput['relatedMetric']>
> = ({
  values,
  options,
  touched,
  error,
  conditions,
}: FormFieldProps<
  CoachingDetailsInput['relatedMetric']
>): React.ReactElement => {
  const intl = useIntl();

  return (!conditions?.developmentTopic && conditions?.contactSelected) ||
    conditions?.contactOptional ? (
    <Field
      as={Select}
      name='relatedMetric'
      label={intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_label_required_related_metric',
        defaultMessage: 'Related metric *',
        description: 'Coaching Details Metric',
      })}
      value={values}
      options={options}
      search
      errorText={touched && error}
    />
  ) : (
    <> </>
  );
};

export const Action: React.FC<
  FormFieldProps<CoachingDetailsInput['assignedAction']>
> = ({
  values,
  touched,
  error,
  conditions,
}: FormFieldProps<
  CoachingDetailsInput['assignedAction']
>): React.ReactElement => {
  const intl = useIntl();
  return conditions?.developmentTopic ||
    conditions?.contactSelected ||
    conditions?.contactOptional ? (
    <Field
      data-cy='coaching-details-action-item'
      as={Textarea}
      name='assignedAction'
      label={intl.formatMessage({
        id: 'cs_perfportal_coaching_detail_label_required_assigned_action',
        defaultMessage: 'Assigned action *',
        description: 'Coaching Details Assigned Action Label',
      })}
      value={values}
      maxLength={COACHING_TEXT_LIMIT}
      errorText={touched && error}
    />
  ) : (
    <> </>
  );
};
